import React from 'react';
import PropTypes from 'prop-types';
import {
  Editor,
  EditorState,
  Modifier,
  CompositeDecorator,
  ContentState,
  convertToRaw,
  convertFromRaw,
  getDefaultKeyBinding,
  SelectionState
} from 'draft-js';
import PubSub from 'pubsub-js';
import { diffChars } from 'diff';
import { splitSentences } from '../../utils/string';

import Playlist from '../Playlist';
import ButtonPlayer from '../ButtonPlayer';

import 'draft-js/dist/Draft.css';

const SeparatorDecorator = ({ bgColor = "bg-info", textColor = "text-info", children }) => {
  return <span className={`inline-block bg-opacity-25 px-2 font-bold ${bgColor} ${textColor} js-tooltip`}>
    {children}
    <div className="js-tooltip-element font-bold">
      Délimitateur de morceau
      <div className="js-tooltip-arrow" data-popper-arrow></div>
    </div>
  </span>;
};

const InvisibleSeparatorDecorator = () => {
  return <span className={`inline-block bg-opacity-25 font-bold bg-grey-light text-grey-light`}>{" "}</span>;
};

const SelectionDecorator = ({ children }) => {
  return <span className={`py-1 bg-opacity-25 bg-warning font-bold border border-warning border-dotted js-tooltip`}>
    {children}
    <div className="js-tooltip-element font-bold">
      {`Pause de ${children[0].props.text.replace('#', '')}ms`}
      <div className="js-tooltip-arrow" data-popper-arrow></div>
    </div>
  </span>;
};

const BreakDecorator = ({ bgColor = "bg-warning", textColor = "text-warning", children }) => {
  return <span className={`inline-block bg-opacity-25 px-2 font-bold ${bgColor} ${textColor} js-tooltip`}>
    {children}
    <div className="js-tooltip-element font-bold">
      {`Pause de ${children[0].props.text.replace('#', '')}ms`}
      <div className="js-tooltip-arrow" data-popper-arrow></div>
    </div>
  </span>;
};

const NonBreakingDecorator = ({ bgColor = "bg-danger", textColor = "text-danger", children }) => {
  return <span className={`inline-block bg-danger`}>
    {children}
  </span>;
};

const SentenceDecorator = ({ borderColor = "border-info", textColor = "text-info", children }) => {
  return <span className={`inline-block border-2 font-bold px-px ${textColor} ${borderColor} js-tooltip`}>
    {children}
    <div className="js-tooltip-element font-bold">
      Fin de phrase
      <div className="js-tooltip-arrow" data-popper-arrow></div>
    </div>
  </span>;
};

const SentenceInQuoteDecorator = ({ children }) => {
  return children;
};

class TextEditor extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.specialCharRegex = new RegExp(`(${gon.specialCharRegex})`, 'mgi');
    this.state = {
      localStorageEnabled: this.localStorageEnabled(),
      wordCount: 0,
      mode: this.props.content_with_errors_input_name ? "editor" : "text",
      editorState: this.moveSelectionToEnd(this.setDefaultEditorState("value", true, false, this.props.content_with_errors_value ? [] : false)),
      phoneticEditorState: this.setDefaultEditorState("phonetic_value", false, true),
      contentWithErrorsEditorState: this.setDefaultEditorState("content_with_errors_value", false, true)
    };

    this.setDefaultEditorState = this.setDefaultEditorState.bind(this);
    this.selectMode = this.selectMode.bind(this);
    this.localStorageEnabled = this.localStorageEnabled.bind(this);
    this.localStorageItemName = this.localStorageItemName.bind(this);
    this.saveContent = this.saveContent.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.broadcastChange = this.broadcastChange.bind(this);
  }

  setDomEditorRef = ref => this.domEditor = ref;

  setDefaultEditorState (valueName = "value", fallback = true, phonetic = false, decorators = []) {
    if (this.props[valueName] && this.props[valueName].length) {
      return EditorState.createWithContent(ContentState.createFromText(this.props[valueName]), this.createDecorator(phonetic, decorators));
    } else if (fallback) {
      const content = this.props.dictation_id && this.localStorageEnabled() ? window.localStorage.getItem(this.localStorageItemName()) : null;
      if (content && content.length) {
        return EditorState.createWithContent(convertFromRaw(JSON.parse(content)), this.createDecorator(phonetic, decorators));
      }
    }

    return EditorState.createEmpty(this.createDecorator(phonetic, decorators));
  }

  handlePlaylistShortcut = (event, stateName = undefined) => {
    if (this.props.keyBinding) {
      if ([13, 117, 9, 118].indexOf(event.keyCode) >= 0) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    }

    if (this.props.editable) {
      if ([13].indexOf(event.keyCode) >= 0) {
        event.preventDefault();
        event.stopPropagation();
        this.insertSeparator(stateName);
        return false;
      }
    }

    return getDefaultKeyBinding(event);
  }

  componentDidMount () {
    if (this.domEditor && this.props.autoFocus) this.domEditor.focus();

    window.domEditor = this.domEditor;

    PubSub.subscribe('DICTATION_INSERT_TEXT', (_msg, data) => this.insertText(data));

    setTimeout(() => {
      this.broadcastChange("editorState");
    }, 300);
  }

  componentWillUnmount = () => {
    window.domEditor = null;
  }

  createDecorator = (phonetic = false, decorators = []) => {
    if (!this.props.editable || !this.props.decorators) return null;

    if (!decorators) {
      return new CompositeDecorator([
        {
          strategy: (contentBlock, callback) => this.handleStrategy(this.props.highlighted_words, contentBlock, callback),
          component: InvisibleSeparatorDecorator
        }
      ])
    }

    const props = phonetic ? {
      bgColor: "bg-danger",
      textColor: "text-danger",
      borderColor: "border-danger"
    } : {
      bgColor: "bg-info",
      textColor: "text-info",
      borderColor: "border-info"
    }

    if (window.site && window.site.tooltips) window.site.tooltips.init();

    return new CompositeDecorator([
      ...decorators,
      ...phonetic ? [
        {
          strategy: (contentBlock, callback) => this.handleStrategy(this.props.break_words, contentBlock, callback),
          component: BreakDecorator
        }
      ] : [],
      {
        strategy: (contentBlock, callback) => this.handleStrategy(this.props.highlighted_words, contentBlock, callback),
        component: SeparatorDecorator,
        props: props
      },
      {
        strategy: (contentBlock, callback) => this.newSentenceCharInQuote(contentBlock, callback),
        component: SentenceInQuoteDecorator,
        props: props
      },
      {
        strategy: (contentBlock, callback) => this.handleStrategy([new RegExp(gon.newSentenceCharRegex, 'mgi')], contentBlock, callback),
        component: SentenceDecorator,
        props: props
      },
      {
        strategy: (contentBlock, callback) => this.handleStrategy([new RegExp(' ', 'mgi')], contentBlock, callback),
        component: NonBreakingDecorator,
        props: props
      }
    ]);
  }

  findWithRegex = (words, contentBlock, callback) => {
    const text = contentBlock.getText();

    words.forEach(word => {
      const matches = [...text.matchAll(word)];
      matches.forEach(match =>
        callback(match.index, match.index + match[0].length, match[0].trim())
      );
    });
  }

  handleStrategy = (words, contentBlock, callback) => {
    this.findWithRegex(words, contentBlock, callback);
  }

  newSentenceCharInQuote = (contentBlock, callback) => {
    const text = contentBlock.getText();
    const quoteRegex = new RegExp('«[^»]+»', 'mgi');
    const newSentenceCharRegex = new RegExp(gon.newSentenceCharRegex, 'mgi');

    let matchArrQuotes, matchArrNewSentenceChar, startQuotes, startNewSentenceChar;

    while ((matchArrQuotes = quoteRegex.exec(text)) !== null) {
      startQuotes = matchArrQuotes.index;

      while ((matchArrNewSentenceChar = newSentenceCharRegex.exec(matchArrQuotes[0])) !== null) {
        startNewSentenceChar = matchArrNewSentenceChar.index;

        callback(startQuotes + startNewSentenceChar, startQuotes + startNewSentenceChar + matchArrNewSentenceChar[0].length);
      }
    }
  }

  insertSeparator (stateName = undefined) {
    this.insertText('|', stateName);
  }

  insertText (text, stateName = "editorState") {
    const nextContentState = Modifier.insertText(
      this.state[stateName].getCurrentContent(),
      this.state[stateName].getSelection(),
      text
    );
    const nextEditorState = EditorState.push(this.state[stateName], nextContentState, 'insert-characters');
    this.onChange(nextEditorState, stateName);
    if (this.props.autoFocus) this.domEditor.focus();
  }

  moveSelectionToEnd = editorState => {
    if (window.innerWidth < 1024 || !this.props.autoFocus) return editorState;

    const content = editorState.getCurrentContent();
    const blockMap = content.getBlockMap();

    const key = blockMap.last().getKey();
    const length = blockMap.last().getLength();

    const selection = new SelectionState({
      anchorKey: key,
      anchorOffset: length,
      focusKey: key,
      focusOffset: length,
    });

    return EditorState.forceSelection(editorState, selection);
  }

  localStorageEnabled = () => {
    if (this.state && this.state.localStorageEnabled != undefined) return this.state.localStorageEnabled;

    var test = 'test';
    try {
      window.localStorage.setItem(test, test);
      window.localStorage.removeItem(test);
      return true;
    } catch(e) {
      return false;
    }
  }
  localStorageItemName = () => `dictation-${this.props.dictation_id}`;

  saveContent = (content) => {
    window.localStorage.setItem(this.localStorageItemName(), JSON.stringify(convertToRaw(content)));
  }

  onChange = (editorState, stateName = "editorState", preserveSelectedText = false) => {
    let sanitizedEditorState = editorState;

    this.setState(prevState => {
      if (stateName == "editorState" && this.props.phonetic_input_name && prevState["editorState"].getCurrentContent().getPlainText() == prevState["phoneticEditorState"].getCurrentContent().getPlainText()) {
        const nextEditorState = EditorState.push(this.state.phoneticEditorState, sanitizedEditorState.getCurrentContent(), 'insert-text');
        prevState["phoneticEditorState"] = nextEditorState;
      }

      if (stateName == "editorState" && this.props.content_with_errors_input_name && prevState["editorState"].getCurrentContent().getPlainText() == prevState["contentWithErrorsEditorState"].getCurrentContent().getPlainText()) {
        const nextEditorState = EditorState.push(this.state.contentWithErrorsEditorState, sanitizedEditorState.getCurrentContent(), 'insert-text');
        prevState["contentWithErrorsEditorState"] = nextEditorState;
      }

      let text, selectedText;

      if (preserveSelectedText) {
        selectedText = prevState.selectedText;
      } else {
        text = sanitizedEditorState.getCurrentContent().getPlainText(),
        selectedText = text.slice(sanitizedEditorState.getSelection().getStartOffset(), sanitizedEditorState.getSelection().getEndOffset())

        prevState.selectedText = selectedText;
      }

      sanitizedEditorState = EditorState.set(sanitizedEditorState, { decorator: this.createDecorator(stateName == "phoneticEditorState", this.state.mode == "text" ? false : selectedText ? [
        {
          strategy: (contentBlock, callback) => this.handleStrategy([selectedText.replace(")", "\\)").replace("(", "\\(")], contentBlock, callback),
          component: SelectionDecorator
        }
      ] : [])});

      prevState[stateName] = sanitizedEditorState;

      return prevState;
    }, () => {
      this.broadcastChange(stateName);
    });
  }

  broadcastChange (stateName = "editorState") {
    if (stateName == "editorState" && this.props.broadcastChange) {
      if (this.props.dictation_id && this.localStorageEnabled()) this.saveContent(this.state[stateName].getCurrentContent());
      PubSub.publish('DICTATION_ON_CHANGE', this.state[stateName]);
    } else if (stateName == "contentWithErrorsEditorState" && this.props.broadcastChange) {
      PubSub.publish('DICTATION_COUNT_ERRORS', {
        initialValues: this.state.editorState.getCurrentContent().getPlainText().split(' ').flatMap(word => word.split(this.specialCharRegex)).filter(Boolean),
        values: this.state.contentWithErrorsEditorState.getCurrentContent().getPlainText().split(' ').flatMap(word => word.split(this.specialCharRegex)).filter(Boolean)
      });
    }
  }

  selectMode (mode) {
    this.setState({ mode, selectedText: null }, () => {
      this.onChange(this.state.editorState);
    })
  }

  onFocus () {
    if (window.innerWidth >= 1024 || !this.props.autoFocus) return;

    setTimeout(() => {
      window.site.scrollTo(
        this.domEditor.editor,
        250,
        window.site.getElementY(this.domEditor.editor)
      );
    }, 250);
  }

  onBlur () {
    if (window.innerWidth >= 1024 || !this.props.autoFocus) return;

    window.site.scrollTo(
      this.domEditor.editor,
      250,
      window.site.getElementY(this.domEditor.editor)
    );
  }

  sanitizeText (editorState, stateName = "editorState") {
    const content = editorState.getCurrentContent()
      .getPlainText()
      .replace(/ +/g, ' ')
      .replace(/é/g, 'é')
      .replace(/ê/g, 'ê')
      .replace(/ù/g, 'ù')
      .replace(/’/g, "'")
      .replace(/([«"].+)(")/g, '$1»')
      .replace(/"/g, '«')
      .replace(/ ?« ?/g, ' « ')
      .replace(/ ?» ?/g, ' » ')
      .replace(/ ?[\.]{3}/g, '…')
      .replace(/‑/g, '-')
      .replace(/ , ?/g, ', ')
      .replace(/ \. ?/g, '. ')
      .replace(/ ?\? ?/g, ' ? ')
      .replace(/ ?\! ?/g, ' ! ')
      .replace(/ +/g, ' ')
      .replace(/ *\| */g, '|')
      .replace(/ *#(\d+)? */g, "#$1")
      .trim()

    if (content && content.length) {
      let newState;

      if (stateName == "editorState") {
        newState = EditorState.createWithContent(
          ContentState.createFromText(content),
          this.createDecorator(false, this.props.content_with_errors_value ? [] : false)
        );
      } else {
        newState = EditorState.createWithContent(
          ContentState.createFromText(content),
          this.createDecorator(true, [])
        );
      }

      setTimeout(() => {
        this.onChange(newState, stateName, true);
      }, 300);
    }
  }

  render() {
    const chunkDelimitatorRegex = new RegExp(gon.chunkDelimitatorRegex, 'mgi');

    const input_value = this.state.editorState.getCurrentContent().getPlainText();
    const phonetic_input_value = this.props.phonetic_input_name && this.state.phoneticEditorState.getCurrentContent().getPlainText();
    const content_with_errors_input_value = this.props.content_with_errors_input_name && this.state.contentWithErrorsEditorState.getCurrentContent().getPlainText();
    let dictation, textWithDiff;

    if (this.state.mode === "phonetic" || this.state.mode === "content_with_errors") {
      const diff = diffChars(input_value, this.state.mode === "phonetic" ? phonetic_input_value : content_with_errors_input_value);
            textWithDiff = document.createElement('div');

      diff.forEach((part) => {
        const color = part.added ? 'success' : part.removed ? 'danger' : 'grey';
        let span = document.createElement('span');
        span.classList.add(`bg-${color}`, `${color == "grey" ? "bg-opacity-0" : "bg-opacity-25"}`, `text-${color}`, `${color == "grey" ? "font-normal" : "font-bold"}`);
        span.appendChild(document.createTextNode(part.value));
        textWithDiff.appendChild(span);
      });
    }

    if (this.props.editable) {
      dictation = splitSentences(phonetic_input_value || content_with_errors_input_value || input_value).map((sentence, sentence_index) => {
        return sentence.trim().split(chunkDelimitatorRegex).map((chunk, chunk_index) => {
          return {
            sentence: sentence_index + 1,
            chunk: chunk_index + 1,
            type: "dictation",
            filename: `${sentence_index}_${chunk_index}.mp3`,
            url: `/api/v1/vocalize?text=${encodeURIComponent(chunk)}`
          };
        });
      });
    }

    return (
      <div className={this.props.class}>
        { this.props.input_name && (<input type="hidden" name={this.props.input_name} value={input_value} />) }
        { this.props.phonetic_input_name && (<input type="hidden" name={this.props.phonetic_input_name} value={phonetic_input_value} />) }
        { this.props.content_with_errors_input_name && (<input type="hidden" name={this.props.content_with_errors_input_name} value={content_with_errors_input_value} />) }

        { this.props.editable && (
          <div className="inline-block text-center float-right -mt-16 -mr-6">
            { this.props.phonetic_input_name && (
              <ul className="bg-grey-light rounded-full inline-block border border-grey-light">
                <li className="inline-block align-center float-left">
                  <span className={`inline-block cursor-pointer px-3 py-1 text-sm font-semibold transition duration-100 ease-in-out group hover:bg-white hover:shadow-lg pl-1 rounded-l-full ${this.state.mode === "text" ? "bg-white" : ""}`} onClick={() => this.selectMode("text")}>
                    <span className={`w-6 h-6 rounded-full ${this.state.mode === "text" ? "bg-grey-light" : "bg-white group-hover:bg-grey-light"} text-sm text-black font-bold inline-block text-center leading-6 mr-1`}>1</span> <span className="inline-block align-middle w-4 -mt-1 mr-1">✏️</span> Texte
                  </span>
                </li>
                <li className="inline-block align-center float-left">
                  <span className={`inline-block cursor-pointer pl-2 pr-3 py-1 text-sm font-semibold transition duration-100 ease-in-out group hover:bg-white hover:shadow-lg ${this.state.mode === "editor" ? "bg-white" : ""}`} onClick={() => this.selectMode("editor")}>
                    <span className={`w-6 h-6 rounded-full ${this.state.mode === "editor" ? "bg-grey-light" : "bg-white group-hover:bg-grey-light"} text-sm text-black font-bold inline-block text-center leading-6 mr-1`}>2</span> <span className="inline-block align-middle w-4 -mt-1 mr-1">✂️</span> Texte découpé
                  </span>
                </li>
                <li className="inline-block align-center float-left">
                  <span className={`inline-block cursor-pointer pl-2 py-1 text-sm font-semibold transition duration-100 ease-in-out group hover:bg-white hover:shadow-lg pr-4 rounded-r-full ${this.state.mode === "phonetic" ? "bg-white" : ""}`} onClick={() => this.selectMode("phonetic")}>
                    <span className={`w-6 h-6 rounded-full ${this.state.mode === "phonetic" ? "bg-grey-light" : "bg-white group-hover:bg-grey-light"} text-sm text-black font-bold inline-block text-center leading-6 mr-1`}>3</span> <span className="inline-block align-middle w-4 -mt-1 mr-1">💬</span> Phonétique
                  </span>
                </li>
              </ul>
            ) }
            { this.props.content_with_errors_input_name && (
              <ul className="bg-grey-light rounded-full inline-block border border-grey-light">
                <li className="inline-block align-center float-left">
                  <span className={`inline-block cursor-pointer px-3 py-1 text-sm font-semibold transition duration-100 ease-in-out group hover:bg-white hover:shadow-lg pl-1 rounded-l-full ${this.state.mode === "editor" ? "bg-white" : ""}`} onClick={() => this.selectMode("editor")}>
                    <span className={`w-6 h-6 rounded-full ${this.state.mode === "editor" ? "bg-grey-light" : "bg-white group-hover:bg-grey-light"} text-sm text-black font-bold inline-block text-center leading-6 mr-1`}>1</span> <span className="inline-block align-middle w-4 -mt-1 mr-1">✏️</span> Texte
                  </span>
                </li>
                <li className="inline-block align-center float-left">
                  <span className={`inline-block cursor-pointer pl-2 py-1 text-sm font-semibold transition duration-100 ease-in-out group hover:bg-white hover:shadow-lg pr-4 rounded-r-full ${this.state.mode === "content_with_errors" ? "bg-white" : ""}`} onClick={() => this.selectMode("content_with_errors")}>
                    <span className={`w-6 h-6 rounded-full ${this.state.mode === "content_with_errors" ? "bg-grey-light" : "bg-white group-hover:bg-grey-light"} text-sm text-black font-bold inline-block text-center leading-6 mr-1`}>2</span> <span className="inline-block align-middle w-4 -mt-1 mr-1">🕵️‍♂️</span> Texte avec fautes
                  </span>
                </li>
              </ul>
            ) }
          </div>
        ) }
        { (this.state.mode === "editor" || this.state.mode === "text" || !this.props.editable) && (
          <div className="h-full">
            <Editor
              ref={this.setDomEditorRef}
              keyBindingFn={event => this.handlePlaylistShortcut(event)}
              editorState={this.state.editorState}
              onChange={this.onChange}
              onFocus={this.onFocus}
              onBlur={() => this.props.editable ? this.sanitizeText(this.state.editorState) : this.onBlur}
              preserveSelectionOnBlur={true}
              placeholder="Tapez votre texte ici ..."
              handlePastedText={this.props.allowPaste ? undefined : () => true}
            />
            { this.props.editable && this.props.allowListen && this.state.selectedText && this.state.selectedText.length > 0 && !this.props.content_with_errors_input_name && (
              <div className={`mt-3 mb-${this.props.phonetic_input_name && input_value !== phonetic_input_value ? 6 : 0}`}>
                <ButtonPlayer text={this.state.selectedText}>Écouter la sélection</ButtonPlayer>
              </div>
            ) }
            { this.props.phonetic_input_name && input_value !== phonetic_input_value && (
              <div>
                <div className="w-8 mt-4 mb-5 border-t border-grey-mid"></div>
                <p className="mt-4 text-base">⚠️ <span className="font-bold">Pensez à reporter vos modifications dans la version phonétique.</span></p>
              </div>
            ) }
            { this.props.content_with_errors_input_name && input_value !== content_with_errors_input_value && (
              <div>
                <div className="w-8 mt-4 mb-5 border-t border-grey-mid"></div>
                <p className="mt-4 text-base">⚠️ <span className="font-bold">Pensez à reporter vos modifications dans la version avec fautes.</span></p>
              </div>
            ) }
          </div>
        ) }
        { this.state.mode === "phonetic" && (
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <Editor
                onBlur={() => this.sanitizeText(this.state.phoneticEditorState, "phoneticEditorState")}
                ref={this.setDomEditorRef}
                editorState={this.state.phoneticEditorState}
                onChange={editorState => this.onChange(editorState, "phoneticEditorState")}
                placeholder="Tapez votre texte ici ..."
                preserveSelectionOnBlur={true}
                keyBindingFn={event => this.handlePlaylistShortcut(event, "phoneticEditorState")}
                handlePastedText={this.props.allowPaste ? undefined : () => true}
              />
              { this.state.selectedText && this.state.selectedText.length > 0 && (
                <div className="mt-3 mb-6">
                  <ButtonPlayer text={this.state.selectedText}>Écouter la sélection</ButtonPlayer>
                </div>
              ) }
              <div>
                <div className="w-8 mt-4 mb-5 border-t border-grey-mid"></div>
                <p className="mt-4 text-base font-bold">Différence avec la version texte :</p>
                { input_value !== phonetic_input_value ? (
                  <div className="mt-4" dangerouslySetInnerHTML={{ __html: textWithDiff.innerHTML }} />
                ) : (
                  <div className="mt-4 text-grey">Aucune</div>
                ) }
              </div>
            </div>
            <div className="col-span-1 h-full">
              <div className="bg-black-mid p-4 rounded-lg h-full">
                {
                  <Playlist dictation={dictation} />
                }
              </div>
            </div>
          </div>
        ) }
        { this.state.mode === "content_with_errors" && (
          <div>
            <Editor
              onBlur={() => this.sanitizeText(this.state.contentWithErrorsEditorState, "contentWithErrorsEditorState")}
              editorState={this.state.contentWithErrorsEditorState}
              onChange={editorState => this.onChange(editorState, "contentWithErrorsEditorState")}
              placeholder="Tapez votre texte ici ..."
              handlePastedText={this.props.allowPaste ? undefined : () => true}
            />
            <div>
              <div className="w-8 mt-4 mb-5 border-t border-grey-mid"></div>
              <p className="mt-4 text-base font-bold">Différence avec la version texte :</p>
              { input_value !== content_with_errors_input_value ? (
                <div className="mt-4" dangerouslySetInnerHTML={{ __html: textWithDiff.innerHTML }} />
              ) : (
                <div className="mt-4 text-grey">Aucune</div>
              ) }
            </div>
          </div>
        ) }
      </div>
    );
  }
}

TextEditor.propTypes = {
  dictation_id: PropTypes.string,
  editable: PropTypes.bool,
  broadcastChange: PropTypes.bool,
  keyBinding: PropTypes.bool,
  input_name: PropTypes.string,
  phonetic_input_name: PropTypes.string,
  content_with_errors_input_name: PropTypes.string,
  value: PropTypes.string,
  highlighted_words: PropTypes.array,
  break_words: PropTypes.array,
  class: PropTypes.string,
  allowPaste: PropTypes.bool,
  decorators: PropTypes.bool,
  allowListen: PropTypes.bool,
  autoFocus: PropTypes.bool
};

TextEditor.defaultProps = {
  editable: false,
  broadcastChange: true,
  keyBinding: true,
  highlighted_words: [/\|/g],
  break_words: [/#(\d+)?/g],
  class: "",
  allowPaste: false,
  decorators: true,
  allowListen: true,
  autoFocus: true
};

export default TextEditor;
